import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Route, useHistory, useLocation } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';

import ErrorPageNotLogged from 'components/ErrorPageNotLogged';
import PlayBar from 'components/PlayBar';
import RequestAccessPage from 'components/RequestAccessPage';
import SearchPanel from 'components/SearchPanel';
import { UserStatuses } from 'components/UI/StatusCard/data';
import Header from 'containers/Header';
import Main from 'containers/Main';

import { useMemoSelector } from 'hooks';
import Api from 'services/Api';
import { updateUser } from 'services/Api';
import { getAllRoutes, getUserStatus, setWorkTags } from 'store';
import { getIsNeedLogin, getNormalizedRoute, mapTagsToWorkTags, Paths, universalPaths } from 'utils';

import { ReactComponent as LoadingGray } from 'assets/Loading_1.svg';
import { ReactComponent as LoadingFilled } from 'assets/Loading_2.svg';
import styles from './Layout.module.scss';

const Layout = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const history = useHistory();

  const { userStatus, allRoutes } = useMemoSelector((state) => ({
    userStatus: getUserStatus(state),
    allRoutes: getAllRoutes(state),
  }));

  const [isNotLogged404, setIsNotLogged404] = useState<boolean>(false);
  const [noAccess, setNoAccess] = useState<boolean>(false);

  const {
    oktaAuth,
    authState: { isAuthenticated, accessToken, refreshToken },
  } = useOktaAuth();

  useEffect(() => {
    if (userStatus && userStatus !== UserStatuses.ACTIVE && universalPaths.includes(location.pathname as Paths)) {
      setNoAccess(true);
    }
  }, [location.pathname, userStatus]);

  useEffect(() => {
    const needLogin = getIsNeedLogin(isAuthenticated, accessToken, refreshToken);

    const { id } = getNormalizedRoute(allRoutes, location.pathname);

    if (needLogin) {
      if (id) {
        if (universalPaths.includes(location.pathname as Paths)) {
          setNoAccess(true);
          return;
        }
        history.push('/', { path: location.pathname });
      } else {
        setIsNotLogged404(true);
      }
      return;
    }

    (async () => {
      await updateUser();
      Api.addSignedCookies();
    })();

    (async () => {
      const tagsResponse = await Api.getWorkTags();
      if (!tagsResponse) return dispatch(setWorkTags());
      dispatch(setWorkTags(mapTagsToWorkTags(tagsResponse)));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, dispatch, oktaAuth]);

  if (userStatus !== UserStatuses.ACTIVE && noAccess && universalPaths.includes(location.pathname as Paths)) {
    return (
      <>
        {allRoutes
          .filter((el) => el.universal)
          .map(({ id, path, component, componentPublic }) => (
            <Route key={id} path={path} component={componentPublic || component} />
          ))}
      </>
    );
  }

  return !userStatus && !isNotLogged404 ? (
    <div className={styles.loadingContainer}>
      <div className={styles.loadingLayout}>
        <LoadingGray />
        <div className={styles.frame}>
          <LoadingFilled />
        </div>
      </div>
    </div>
  ) : userStatus === UserStatuses.ERROR || isNotLogged404 ? (
    <ErrorPageNotLogged />
  ) : userStatus !== UserStatuses.ACTIVE ? (
    <RequestAccessPage />
  ) : (
    <>
      <SearchPanel />
      <Header />
      <PlayBar />
      <Main />
    </>
  );
};

export default Layout;
